/**
 * useConfirm — promise-based confirmation dialog built on the app `Modal`.
 *
 * Replaces native `window.confirm` so confirmations match the app's chrome
 * (sharp corners, paper bg, Fraunces title) and theme. Keeps the imperative
 * call shape of `window.confirm` so existing control flow barely changes:
 *
 *   const { confirm, dialog } = useConfirm();
 *   // ...
 *   if (!(await confirm({ title: 'Smazat?', tone: 'danger' }))) return;
 *   // ...render {dialog} once in the component tree
 *
 * `isOpenRef` lets window-level keydown handlers that share the Escape key
 * with the dialog bail while it's open (see the form cancel guards).
 */

import { useCallback, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { Button } from './Button';
import { Modal, DialogCrown, type ModalTone } from './Modal';

export interface ConfirmOptions {
  /** Display title (Fraunces serif). Wrap italics in `<em>`. */
  title: ReactNode;
  /** Optional sub-paragraph under the title. */
  description?: ReactNode;
  /** Mono uppercase pip row. Defaults to "Potvrzení". */
  eyebrow?: ReactNode;
  /** Pip + accent tone. Defaults to "warn". */
  tone?: ModalTone;
  /** Primary button label. Defaults to "Potvrdit". */
  confirmLabel?: string;
  /** Secondary button label. Defaults to "Zrušit". */
  cancelLabel?: string;
}

export interface UseConfirmResult {
  /** Open the dialog; resolves true on confirm, false on cancel/dismiss. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Render this once in the component tree. */
  dialog: ReactNode;
  /** True while the dialog is open — read in window keydown guards. */
  isOpenRef: MutableRefObject<boolean>;
}

export function useConfirm(): UseConfirmResult {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);
  const isOpenRef = useRef(false);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
    setOpen(true);
    isOpenRef.current = true;
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    setOpen(false);
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(result);
    // Keep `isOpenRef` true until the current event dispatch finishes so an
    // Escape keypress that dismisses this dialog doesn't also re-trigger the
    // window keydown handler that opened it — both fire in the same dispatch.
    queueMicrotask(() => {
      isOpenRef.current = false;
    });
  }, []);

  const dialog = (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) settle(false);
      }}
      maxWidth={460}
      ariaLabel="Potvrzení"
    >
      {options && (
        <>
          <DialogCrown
            tone={options.tone ?? 'warn'}
            eyebrow={options.eyebrow ?? 'Potvrzení'}
            title={options.title}
            sub={options.description}
          />
          <Modal.Foot
            actions={
              <>
                <Button variant="secondary" onClick={() => settle(false)}>
                  {options.cancelLabel ?? 'Zrušit'}
                </Button>
                <Button variant="primary" onClick={() => settle(true)}>
                  {options.confirmLabel ?? 'Potvrdit'}
                </Button>
              </>
            }
          />
        </>
      )}
    </Modal>
  );

  return { confirm, dialog, isOpenRef };
}
